import { useLocation, useNavigate } from "react-router-dom";

// Supported languages
export const LANGUAGES = ["en", "de"];
export const DEFAULT_LANG = "en";

// Route slugs for each language
export const ROUTE_SLUGS = {
  home: { en: "", de: "" },
  gallery: { en: "gallery", de: "gallery" },
  lab: { en: "lab", de: "lab" },
  services: { en: "services", de: "services" },
  about: { en: "about", de: "about" },
  contact: { en: "contact", de: "contact" },
  principles: { en: "principles", de: "principles" },
  store: { en: "store", de: "store" },
  nyx: { en: "nyx", de: "nyx" },
  invoiceMaker: { en: "invoice-maker", de: "invoice-maker" },
  privacy: { en: "privacy-policy", de: "privacy-policy" },
  disclaimer: { en: "legal-disclaimer", de: "legal-disclaimer" },
  accessibility: { en: "accessibility", de: "accessibility" },
};

// Get user's preferred language from browser settings
export const getBrowserLang = () => {
  if (typeof window !== 'undefined') {
    const browserLang = window.navigator.language || window.navigator.userLanguage || '';
    const lang = browserLang.split('-')[0].toLowerCase();
    
    // Check if browser language is supported
    if (LANGUAGES.includes(lang)) {
      return lang;
    }
    
    // Fallback: if browser language starts with supported language (e.g., 'de-DE' -> 'de')
    const baseLang = browserLang.split('-')[0].toLowerCase();
    if (LANGUAGES.includes(baseLang)) {
      return baseLang;
    }
  }
  return DEFAULT_LANG;
};

// Get current language from URL path
export const getLangFromPath = (pathname = '') => {
  if (typeof window === 'undefined') return DEFAULT_LANG;
  
  const path = pathname || window.location.pathname;
  const segments = path.split('/').filter(Boolean);
  
  if (segments.length > 0 && LANGUAGES.includes(segments[0])) {
    return segments[0];
  }
  
  return DEFAULT_LANG;
};

// Get route paths for current language
export const getRoutePaths = (lang = DEFAULT_LANG) => ({
  home: lang === DEFAULT_LANG ? '/' : `/${lang}`,
  gallery: lang === DEFAULT_LANG ? `/${ROUTE_SLUGS.gallery[lang]}` : `/${lang}/${ROUTE_SLUGS.gallery[lang]}`,
  lab: lang === DEFAULT_LANG ? `/${ROUTE_SLUGS.lab[lang]}` : `/${lang}/${ROUTE_SLUGS.lab[lang]}`,
  services: lang === DEFAULT_LANG ? `/${ROUTE_SLUGS.services[lang]}` : `/${lang}/${ROUTE_SLUGS.services[lang]}`,
  about: lang === DEFAULT_LANG ? `/${ROUTE_SLUGS.about[lang]}` : `/${lang}/${ROUTE_SLUGS.about[lang]}`,
  contact: lang === DEFAULT_LANG ? `/${ROUTE_SLUGS.contact[lang]}` : `/${lang}/${ROUTE_SLUGS.contact[lang]}`,
  principles: lang === DEFAULT_LANG ? `/${ROUTE_SLUGS.principles[lang]}` : `/${lang}/${ROUTE_SLUGS.principles[lang]}`,
  store: lang === DEFAULT_LANG ? `/${ROUTE_SLUGS.store[lang]}` : `/${lang}/${ROUTE_SLUGS.store[lang]}`,
  nyx: lang === DEFAULT_LANG ? `/${ROUTE_SLUGS.nyx[lang]}` : `/${lang}/${ROUTE_SLUGS.nyx[lang]}`,
  invoiceMaker: lang === DEFAULT_LANG ? `/${ROUTE_SLUGS.invoiceMaker[lang]}` : `/${lang}/${ROUTE_SLUGS.invoiceMaker[lang]}`,
  privacy: lang === DEFAULT_LANG ? `/${ROUTE_SLUGS.privacy[lang]}` : `/${lang}/${ROUTE_SLUGS.privacy[lang]}`,
  disclaimer: lang === DEFAULT_LANG ? `/${ROUTE_SLUGS.disclaimer[lang]}` : `/${lang}/${ROUTE_SLUGS.disclaimer[lang]}`,
  accessibility: lang === DEFAULT_LANG ? `/${ROUTE_SLUGS.accessibility[lang]}` : `/${lang}/${ROUTE_SLUGS.accessibility[lang]}`,
});

// Remove language prefix from pathname
export const stripLangFromPath = (pathname) => {
  const segments = pathname.split('/').filter(Boolean);
  const lang = segments[0];
  
  if (LANGUAGES.includes(lang)) {
    return '/' + segments.slice(1).join('/');
  }
  
  return pathname;
};

// Switch language in current path
export const switchLangPath = (pathname, targetLang = DEFAULT_LANG) => {
  const segments = pathname.split('/').filter(Boolean);
  const currentLang = segments[0];
  
  // If current path has a language prefix, replace it
  if (LANGUAGES.includes(currentLang)) {
    const remainingPath = '/' + segments.slice(1).join('/');
    return targetLang === DEFAULT_LANG ? remainingPath : `/${targetLang}${remainingPath}`;
  }
  
  // If no language prefix, add it (except for default language)
  if (targetLang === DEFAULT_LANG) {
    return pathname;
  }
  
  return `/${targetLang}${pathname}`;
};

// Match route key from pathname
export const matchRouteKey = (lang, pathname) => {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  const paths = getRoutePaths(lang);
  const entries = Object.entries(paths);
  
  for (const [key, value] of entries) {
    if (normalized === value) {
      return key;
    }
  }
  return null;
};

// Get all route keys
export const getAllRouteKeys = () => Object.keys(ROUTE_SLUGS);

// Custom hook to get and set current language
export const useLang = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const currentLang = getLangFromPath(location.pathname);
  
  const setLang = (lang) => {
    // Store preference in localStorage
    try {
      window.localStorage.setItem('twk_lang', lang);
    } catch (e) {
      console.warn('Could not save language preference to localStorage:', e);
    }
    
    // Navigate to the same path with new language
    const newPath = switchLangPath(location.pathname, lang);
    navigate(newPath, { replace: true });
  };
  
  const getStoredLang = () => {
    try {
      return window.localStorage.getItem('twk_lang') || null;
    } catch (e) {
      return null;
    }
  };
  
  return {
    currentLang,
    setLang,
    getStoredLang,
    browserLang: getBrowserLang(),
    availableLangs: LANGUAGES,
  };
};

// Get initial language for app (checks localStorage, browser, then default)
export const getInitialLang = () => {
  // Try localStorage first
  try {
    const stored = window.localStorage.getItem('twk_lang');
    if (stored && LANGUAGES.includes(stored)) {
      return stored;
    }
  } catch (e) {
    // localStorage not available
  }
  
  // Fallback to browser language
  const browserLang = getBrowserLang();
  if (LANGUAGES.includes(browserLang)) {
    return browserLang;
  }
  
  // Fallback to default
  return DEFAULT_LANG;
};

// Get language from path or fallback to initial language
export const getLangFromPathWithFallback = (pathname) => {
  const langFromPath = getLangFromPath(pathname);
  const initialLang = getInitialLang();
  
  // If path has valid language, use it
  if (LANGUAGES.includes(langFromPath)) {
    return langFromPath;
  }
  
  // Otherwise use stored or browser preference
  return initialLang;
};